<script setup>
import { computed, onMounted, ref } from 'vue';
import DataDeletionRequestsAPI from 'dashboard/api/dataDeletionRequests';
import Button from 'dashboard/components-next/button/Button.vue';

const requests = ref([]);
const loading = ref(true);
const error = ref('');
const filter = ref('all');
const statuses = ['pending', 'verifying', 'completed', 'rejected'];
const filtered = computed(() => filter.value === 'all' ? requests.value : requests.value.filter(item => item.status === filter.value));

const load = async () => {
  loading.value = true;
  try {
    requests.value = (await DataDeletionRequestsAPI.get()).data;
    error.value = '';
  } catch {
    error.value = 'Deletion requests could not be loaded.';
  } finally {
    loading.value = false;
  }
};

const save = async item => {
  try {
    const response = await DataDeletionRequestsAPI.update(item.id, { status: item.status, resolution_notes: item.resolution_notes });
    Object.assign(item, response.data);
  } catch {
    error.value = 'This deletion request could not be updated.';
  }
};

onMounted(load);
</script>

<template>
  <div class="flex h-full flex-col overflow-auto bg-n-background-1 p-6 lg:p-8">
    <header class="mb-6 flex flex-wrap items-center justify-between gap-4">
      <div>
        <h1 class="text-2xl font-semibold text-n-slate-12">Data Deletion Requests</h1>
        <p class="mt-1 text-sm text-n-slate-11">Privacy requests submitted through the public deletion form</p>
      </div>
      <Button label="Refresh" icon="i-lucide-refresh-cw" slate sm :is-loading="loading" @click="load" />
    </header>

    <div class="mb-5 flex flex-wrap gap-2">
      <button v-for="status in ['all', ...statuses]" :key="status" type="button" class="rounded-full border px-3 py-1.5 text-sm capitalize"
        :class="filter === status ? 'border-n-brand bg-n-brand text-white' : 'border-n-weak text-n-slate-11'" @click="filter = status">{{ status }}</button>
    </div>
    <p v-if="error" class="mb-5 rounded-xl border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-11">{{ error }}</p>
    <p v-if="!loading && !filtered.length" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-8 text-center text-n-slate-11">No data deletion requests in this view.</p>

    <section class="grid gap-4 xl:grid-cols-2">
      <article v-for="item in filtered" :key="item.id" class="rounded-2xl border border-n-weak bg-n-alpha-3 p-5 shadow-sm">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h2 class="font-semibold text-n-slate-12">{{ item.name }}</h2>
            <p class="mt-1 text-sm"><a class="text-n-brand" :href="`tel:${item.phone}`">{{ item.phone }}</a><span v-if="item.email"> · <a class="text-n-brand" :href="`mailto:${item.email}`">{{ item.email }}</a></span></p>
          </div>
          <select v-model="item.status" class="rounded-lg border border-n-weak bg-n-alpha-3 px-3 py-2 text-sm capitalize text-n-slate-12">
            <option v-for="status in statuses" :key="status" :value="status">{{ status }}</option>
          </select>
        </div>
        <div class="mt-4 rounded-xl bg-n-alpha-2 p-4 text-sm">
          <p class="mb-1 text-xs font-medium uppercase tracking-wide text-n-slate-10">Request details</p>
          <p class="whitespace-pre-wrap text-n-slate-12">{{ item.details || 'No additional details provided.' }}</p>
        </div>
        <label class="mt-4 block text-xs font-medium uppercase tracking-wide text-n-slate-10">Internal resolution notes</label>
        <textarea v-model="item.resolution_notes" rows="3" class="mt-2 w-full rounded-xl border border-n-weak bg-n-alpha-3 p-3 text-sm text-n-slate-12" placeholder="Verification and completion notes" />
        <div class="mt-3 flex items-center justify-between gap-3">
          <p class="text-xs text-n-slate-9">Request #{{ item.id }} · {{ new Date(item.created_at).toLocaleString('en-IN') }}</p>
          <Button label="Save" sm @click="save(item)" />
        </div>
      </article>
    </section>
  </div>
</template>
